import * as React from 'react';
import { useFetcher } from '@remix-run/react';
import { CommentForm } from './CommentForm';
import type { CommentSubmitPayload } from '@modules/build-hub-comment/types';
import type { Candidate } from '@modules/build-hub-comment/components/CommentAutoCompleter';
import { CREATE_BUILD_HUB_COMMENT_API_ROUTE } from '~/routes/remix-api.app.build-hub-comment-create';
import { useBuildHubData } from '@modules/build-hub/hooks/use-build-hub-data';

export function CommentBox({
    documentId,
    onPosted,
}: {
    documentId?: string;
    onPosted?: (payload: CommentSubmitPayload) => void;
}) {
    const fetcher = useFetcher();
    const { buildHub } = useBuildHubData();

    const candidates: Candidate[] = buildHub.collaborators.map((c) => ({
        type:
            String(c.id) === String(buildHub.owner.id)
                ? ('owner' as const)
                : ('user' as const),
        id: String(c.id),
        avatar: c.image,
        label: c.name.length > 0 ? c.name : c.email,
    }));

    async function createComment(payload: CommentSubmitPayload) {
        const formData = new FormData();

        if (payload.context?.documentId) {
            formData.append('targetId', payload.context.documentId);
        }

        formData.append('message', payload.message);

        // Always include the owner ID for the current user
        formData.append('ownerId', String(buildHub.owner.id));

        for (const entity of payload.entities) {
            if (entity.type === 'user') {
                formData.append('mentionIds', entity.id);
            } else if (entity.type === 'file') {
                formData.append('fileIds', entity.id);
            }
        }

        // Use fetcher.submit for optimistic updates
        fetcher.submit(formData, {
            method: 'POST',
            action: CREATE_BUILD_HUB_COMMENT_API_ROUTE,
        });

        // Call onPosted immediately for optimistic UI
        onPosted?.(payload);
    }

    return (
        <CommentForm
            documentId={documentId}
            candidates={candidates}
            onSubmit={async (payload) => {
                await createComment(payload);
            }}
        />
    );
}
